import type { components } from "@/api-schema";
import { DmsSystem } from "@/api-schema-enums";
import { normalizePositiveSafeIntegerIdentifier } from "@/common/helpers/normalize-numeric-identifier";
import { config } from "@/config/configuration";
import { getCompanyV1Identifier } from "@/modules/masterdata/company/company-identifiers";

type Company = Pick<components["schemas"]["Company"], "companyIdentifiers">;
type CustomerIdentifier = Pick<components["schemas"]["CustomerIdentifier"], "type" | "identifier">;

interface CustomerV1Reference {
  customerIdentifiers?: readonly CustomerIdentifier[];
  companies?: readonly Company[];
  branch?: { company?: Company } | null;
}

export function getCustomerV1Url(customer: CustomerV1Reference): string | undefined {
  const dmsIdentifiers = customer.customerIdentifiers?.filter(
    ({ type }) =>
      type === DmsSystem.Formel1Ahg || type === DmsSystem.Formel1Ghg || type === DmsSystem.CrossAccountingNumber,
  );
  if (dmsIdentifiers?.length !== 1) {
    return undefined;
  }

  const dmsCustomerNumber = dmsIdentifiers[0].identifier.trim();
  if (!normalizePositiveSafeIntegerIdentifier(dmsCustomerNumber)) {
    return undefined;
  }

  const companies = customer.branch ? [customer.branch.company] : customer.companies;
  if (companies?.length !== 1 || !companies[0]) {
    return undefined;
  }

  const companyIdentifier = getCompanyV1Identifier(companies[0]);
  const v1CompanyId = companyIdentifier ? normalizePositiveSafeIntegerIdentifier(companyIdentifier) : undefined;
  if (!v1CompanyId) {
    return undefined;
  }

  const baseUrl = config.v1ApiBaseUrl?.trim();
  if (!baseUrl) {
    return undefined;
  }

  let url: URL | undefined;
  try {
    url = new URL("/stammdaten/kunden/erfassung.php4", baseUrl);
  } catch (error) {
    if (!(error instanceof TypeError)) {
      throw error;
    }
  }

  if (!url || (url.protocol !== "http:" && url.protocol !== "https:")) {
    console.error("Cannot create customer V1 link: VITE_V1_API_BASE_URL must be an absolute HTTP(S) URL");
    return undefined;
  }

  url.search = new URLSearchParams({
    anw: "kd",
    firma: v1CompanyId,
    kdnr: dmsCustomerNumber,
  }).toString();
  return url.toString();
}
